import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../api/axiosInstance';

const AdminCustomFields = () => {
  const { user } = useAuth();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingField, setEditingField] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    label: '',
    description: '',
    type: 'text',
    options: [],
    required: false,
    maxLength: 255,
    minLength: 0,
    sortOrder: 0,
    adminOnly: false,
    showInProfile: true,
    showInPostInfo: false
  });

  useEffect(() => {
    if (user?.isAdmin) {
      fetchFields();
    }
  }, [user]);

  const fetchFields = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/admin/custom-fields');
      setFields(response.data.fields);
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Laden der Felder');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      label: '',
      description: '',
      type: 'text',
      options: [],
      required: false,
      maxLength: 255,
      minLength: 0,
      sortOrder: 0,
      adminOnly: false,
      showInProfile: true,
      showInPostInfo: false
    });
    setEditingField(null);
    setShowCreateForm(false);
  };

  const handleCreate = () => {
    resetForm();
    setShowCreateForm(true);
  };

  const handleEdit = (field) => {
    setFormData({
      name: field.name,
      label: field.label,
      description: field.description || '',
      type: field.type,
      options: field.options || [],
      required: field.required,
      maxLength: field.maxLength,
      minLength: field.minLength,
      sortOrder: field.sortOrder,
      adminOnly: field.adminOnly,
      showInProfile: field.showInProfile,
      showInPostInfo: field.showInPostInfo
    });
    setEditingField(field._id);
    setShowCreateForm(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);
      setError(null);

      if (!formData.label.trim()) {
        setError('Label ist erforderlich');
        return;
      }

      if (editingField) {
        // Update existing field
        await axiosInstance.patch(`/admin/custom-fields/${editingField}`, formData);
        setMessage('Feld erfolgreich aktualisiert');
      } else {
        // Create new field
        await axiosInstance.post('/admin/custom-fields', formData);
        setMessage('Feld erfolgreich erstellt');
      }

      await fetchFields();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (fieldId) => {
    if (!window.confirm('Feld wirklich deaktivieren? Bestehende Daten bleiben erhalten.')) {
      return;
    }

    try {
      await axiosInstance.delete(`/admin/custom-fields/${fieldId}`);
      setMessage('Feld deaktiviert');
      await fetchFields();
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Löschen');
    }
  };

  const addOption = () => {
    setFormData(prev => ({
      ...prev,
      options: [...prev.options, { value: '', label: '' }]
    }));
  };

  const updateOption = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.map((option, i) => 
        i === index ? { ...option, [field]: value } : option
      )
    }));
  };

  const removeOption = (index) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  if (!user?.isAdmin) {
    return (
      <div className="text-center p-8">
        <div className="alert alert-error">
          <span>Keine Berechtigung für diese Seite</span>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="loading loading-spinner loading-lg"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">🔧 Custom Fields Verwaltung</h1>
            <p className="text-gray-600 mt-2">
              Erweiterte Profil-Felder für Benutzer verwalten
            </p>
          </div>
          
          <button
            onClick={handleCreate}
            className="btn btn-primary"
          >
            ➕ Neues Feld erstellen
          </button>
        </div>
      </div>

      {message && (
        <div className="alert alert-success mb-6">
          <span>{message}</span>
        </div>
      )}

      {error && (
        <div className="alert alert-error mb-6">
          <span>{error}</span>
        </div>
      )}

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="bg-base-100 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {editingField ? '✏️ Feld bearbeiten' : '➕ Neues Feld erstellen'}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Basic Fields */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name (technisch)
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="input input-bordered w-full"
                placeholder="geburtstag, hobbies, etc."
                disabled={editingField} // Name can't be changed after creation
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Label (Anzeige) *
              </label>
              <input
                type="text"
                value={formData.label}
                onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
                className="input input-bordered w-full"
                placeholder="Geburtstag, Hobbies, etc."
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Beschreibung
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="textarea textarea-bordered w-full"
                placeholder="Optionale Beschreibung oder Hilfetext"
                rows={2}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Feld-Typ
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                className="select select-bordered w-full"
              >
                <option value="text">Text (einzeilig)</option>
                <option value="textarea">Text (mehrzeilig)</option>
                <option value="select">Auswahlliste</option>
                <option value="date">Datum</option>
                <option value="number">Zahl</option>
                <option value="url">URL</option>
                <option value="email">E-Mail</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sortierung
              </label>
              <input
                type="number"
                value={formData.sortOrder}
                onChange={(e) => setFormData(prev => ({ ...prev, sortOrder: parseInt(e.target.value) || 0 }))}
                className="input input-bordered w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Min. Länge
              </label>
              <input
                type="number"
                value={formData.minLength}
                onChange={(e) => setFormData(prev => ({ ...prev, minLength: parseInt(e.target.value) || 0 }))}
                className="input input-bordered w-full"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Max. Länge
              </label>
              <input
                type="number"
                value={formData.maxLength}
                onChange={(e) => setFormData(prev => ({ ...prev, maxLength: parseInt(e.target.value) || 255 }))}
                className="input input-bordered w-full"
                min="1"
                max="2000"
              />
            </div>
          </div>

          {/* Options for Select Fields */}
          {formData.type === 'select' && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Auswahloptionen
              </label>
              
              {formData.options.map((option, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={option.value}
                    onChange={(e) => updateOption(index, 'value', e.target.value)}
                    className="input input-bordered flex-1"
                    placeholder="Wert"
                  />
                  <input
                    type="text"
                    value={option.label}
                    onChange={(e) => updateOption(index, 'label', e.target.value)}
                    className="input input-bordered flex-1"
                    placeholder="Anzeige"
                  />
                  <button
                    onClick={() => removeOption(index)}
                    className="btn btn-error btn-sm"
                  >
                    ❌
                  </button>
                </div>
              ))}
              
              <button
                onClick={addOption}
                className="btn btn-outline btn-sm"
              >
                ➕ Option hinzufügen
              </button>
            </div>
          )}

          {/* Checkboxes */}
          <div className="mt-4 space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.required}
                onChange={(e) => setFormData(prev => ({ ...prev, required: e.target.checked }))}
                className="checkbox checkbox-primary mr-3"
              />
              <span>Pflichtfeld</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.adminOnly}
                onChange={(e) => setFormData(prev => ({ ...prev, adminOnly: e.target.checked }))}
                className="checkbox checkbox-primary mr-3"
              />
              <span>Nur Admins können editieren</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.showInProfile}
                onChange={(e) => setFormData(prev => ({ ...prev, showInProfile: e.target.checked }))}
                className="checkbox checkbox-primary mr-3"
              />
              <span>Im Profil anzeigen</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.showInPostInfo}
                onChange={(e) => setFormData(prev => ({ ...prev, showInPostInfo: e.target.checked }))}
                className="checkbox checkbox-primary mr-3"
              />
              <span>Neben Posts anzeigen</span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-2 mt-6">
            <button
              onClick={resetForm}
              className="btn btn-outline"
            >
              Abbrechen
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn btn-primary"
            >
              {saving ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  Speichere...
                </>
              ) : (
                editingField ? '💾 Aktualisieren' : '✅ Erstellen'
              )}
            </button>
          </div>
        </div>
      )}

      {/* Fields List */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th>Name</th>
                <th>Label</th>
                <th>Typ</th>
                <th>Einstellungen</th>
                <th>Status</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {fields.map(field => (
                <tr key={field._id}>
                  <td>
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                      {field.name}
                    </code>
                  </td>
                  <td className="font-medium">{field.label}</td>
                  <td>
                    <span className="badge badge-outline">
                      {field.type}
                    </span>
                  </td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {field.required && (
                        <span className="badge badge-error badge-xs">Pflicht</span>
                      )}
                      {field.adminOnly && (
                        <span className="badge badge-warning badge-xs">Admin</span>
                      )}
                      {field.showInProfile && (
                        <span className="badge badge-info badge-xs">Profil</span>
                      )}
                      {field.showInPostInfo && (
                        <span className="badge badge-success badge-xs">Posts</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${field.active ? 'badge-success' : 'badge-error'}`}>
                      {field.active ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                  <td>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => handleEdit(field)}
                        className="btn btn-sm btn-outline"
                      >
                        ✏️
                      </button>
                      {field.active && (
                        <button
                          onClick={() => handleDelete(field._id)}
                          className="btn btn-sm btn-error"
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {fields.length === 0 && (
            <div className="text-center p-8 text-gray-500">
              <span className="text-4xl mb-4 block">📝</span>
              <p>Noch keine Custom Fields erstellt</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCustomFields;
